import React, { useState } from 'react';

interface ImageProps {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  fill?: boolean;
  className?: string;
  priority?: boolean;
  quality?: number;
  sizes?: string;
  loading?: 'lazy' | 'eager';
  onLoad?: () => void;
  onError?: () => void;
}

const Image: React.FC<ImageProps> = ({
  src,
  alt,
  width,
  height,
  fill = false,
  className = '',
  priority = false,
  quality = 75,
  sizes,
  loading: propLoading,
  onLoad,
  onError,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Determine loading strategy
  const loadingStrategy = priority ? 'eager' : propLoading || 'lazy';

  // Handle image loading
  const handleLoad = () => {
    setLoading(false);
    onLoad?.();
  };

  // Handle image error
  const handleError = () => {
    setError(true);
    setLoading(false);
    onError?.();
  };

  // Generate srcSet for responsive images
  const generateSrcSet = () => {
    if (!width || fill) return undefined;
    
    const widths = [0.5, 1, 1.5, 2].map(scale => Math.round(width * scale));
    return widths
      .map(w => `${generateOptimizedSrc(src, w, quality)} ${w}w`)
      .join(', ');
  };

  // Generate optimized source URL
  const generateOptimizedSrc = (src: string, width?: number, quality?: number) => {
    // You can implement your own image optimization logic here
    // For now, we'll just return the original src
    return src;
  };

  const imgStyle = fill
    ? {
        position: 'absolute',
        height: '100%',
        width: '100%',
        inset: '0px',
        objectFit: 'cover',
      }
    : undefined;

  const wrapperStyle = fill
    ? {
        position: 'relative',
        width: '100%',
        height: '100%',
      }
    : undefined;

  const img = (
    <img
      src={generateOptimizedSrc(src, width, quality)}
      alt={alt}
      width={!fill ? width : undefined}
      height={!fill ? height : undefined}
      className={`${className} ${loading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
      loading={loadingStrategy}
      onLoad={handleLoad}
      onError={handleError}
      style={imgStyle as React.CSSProperties}
      sizes={sizes}
      srcSet={generateSrcSet()}
    />
  );

  if (fill) {
    return (
      <div style={wrapperStyle as React.CSSProperties}>
        {img}
      </div>
    );
  }

  return img;
};

export default Image;